import React, { useCallback, useMemo, useState } from "react";
import {
  Banner,
  Button,
  Card,
  DataTable,
  DatePicker,
  FormLayout,
  Layout,
  Link,
  Page,
  Spinner,
  Stack,
  TextStyle,
} from "@shopify/polaris";

import { useAdmin } from "context/admin";
import { fetcher } from "util/fetch";

function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function endOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

function formatDateParam(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatDateLabel(value) {
  if (!value) return "—";
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString("en-CA");
}

function formatCurrency(value, { allowNA = true } = {}) {
  if ((value === null || value === undefined) && allowNA) {
    return <TextStyle variation="subdued">N/A</TextStyle>;
  }

  const numeric = Number(value || 0);
  return `$${numeric.toFixed(2)}`;
}

async function parseErrorMessage(response) {
  const contentType = response.headers.get("content-type") || "";

  if (contentType.includes("application/json")) {
    const json = await response.json().catch(() => null);
    return json?.message || json?.error || `Request failed (${response.status}).`;
  }

  const text = await response.text().catch(() => "");
  if (text && !text.trim().startsWith("<")) {
    return text;
  }

  return `Request failed (${response.status}).`;
}

export default function PaymentReportPage() {
  const admin = useAdmin();
  const today = new Date();
  const monthStart = startOfMonth(today);
  const monthEnd = endOfMonth(today);

  const [selectedDates, setSelectedDates] = useState({
    start: monthStart,
    end: monthEnd,
  });
  const [{ month, year }, setDate] = useState({
    month: monthStart.getMonth(),
    year: monthStart.getFullYear(),
  });
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [report, setReport] = useState(null);

  const handleMonthChange = useCallback((nextMonth, nextYear) => {
    setDate({ month: nextMonth, year: nextYear });
  }, []);

  const loadReport = useCallback(async () => {
    if (!admin.selectedShopId) return;

    setLoading(true);

    const params = new URLSearchParams({
      start_date: formatDateParam(selectedDates.start),
      end_date: formatDateParam(selectedDates.end),
    });

    try {
      const response = await fetcher(
        `/api/shops/${admin.selectedShopId}/payment_reports.json?${params}`
      );
      if (!response.ok) {
        throw new Error(await parseErrorMessage(response));
      }

      const payload = await response.json();
      setReport(payload);
      setLoaded(true);
    } catch (error) {
      admin.setToast(error?.message || "Failed to load payment report.");
      setReport(null);
      setLoaded(true);
    } finally {
      setLoading(false);
    }
  }, [admin, selectedDates.end, selectedDates.start]);

  const downloadExcel = useCallback(() => {
    if (!admin.selectedShopId) return;

    const startDate = formatDateParam(selectedDates.start);
    const endDate = formatDateParam(selectedDates.end);
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
    });

    admin.downloadFromLink(
      `/api/shops/${admin.selectedShopId}/payment_reports.xlsx?${params}`,
      `payment-report-${startDate}-to-${endDate}.xlsx`
    );
  }, [admin, selectedDates.end, selectedDates.start]);

  const shopifyDomain = admin.selectedShop?.shopify_domain;

  const rows = useMemo(() => {
    if (!report?.orders?.length) return [];

    const dataRows = report.orders.map((order) => {
      const label = order.order_number || order.order_id;
      const orderCell =
        shopifyDomain && order.order_id ? (
          <Link
            url={`https://${shopifyDomain}/admin/orders/${order.order_id}`}
            external
          >
            {label}
          </Link>
        ) : (
          label
        );

      return [
        orderCell,
        formatDateLabel(order.date),
        order.customer || "Unknown",
        order.service_type || "—",
        formatCurrency(order.reg_total, { allowNA: false }),
        formatCurrency(order.shipping, { allowNA: false }),
        formatCurrency(order.fees_applied),
        formatCurrency(order.taxes, { allowNA: false }),
        formatCurrency(order.total_bank),
      ];
    });

    const totals = report.totals || {};
    dataRows.push([
      <strong key="totals-label">Totals</strong>,
      "",
      "",
      "",
      formatCurrency(totals.reg_total, { allowNA: false }),
      formatCurrency(totals.shipping, { allowNA: false }),
      formatCurrency(totals.fees_applied, { allowNA: false }),
      formatCurrency(totals.taxes, { allowNA: false }),
      formatCurrency(totals.total_bank, { allowNA: false }),
    ]);

    return dataRows;
  }, [report, shopifyDomain]);

  const showInfoBanner = loaded && report?.payout_date_range;
  const showWarningBanner =
    loaded &&
    report &&
    report.shopify_payments_available === false;

  const hasOrders = report?.orders?.length > 0;
  const orderRangeStart = formatDateParam(selectedDates.start);
  const orderRangeEnd = formatDateParam(selectedDates.end);

  return (
    <Page title="Payment Report">
      <Layout>
        <Layout.Section>
          {showInfoBanner ? (
            <div style={{ marginBottom: "1rem" }}>
              <Banner status="info">
                <p>
                  Rows are filtered by order created date. Order range:{" "}
                  {orderRangeStart} to {orderRangeEnd}. Fee data scanned from
                  Shopify Payouts issued{" "}
                  {report.payout_date_range.min} to{" "}
                  {report.payout_date_range.max}. Orders with pending payouts
                  will show N/A for Fees and Total (Bank).
                </p>
              </Banner>
            </div>
          ) : null}

          {showWarningBanner ? (
            <div style={{ marginBottom: "1rem" }}>
              <Banner status="warning">
                <p>
                  Shopify Payments fee data is not available for this store.
                  Fees and bank totals are shown as N/A.
                </p>
              </Banner>
            </div>
          ) : null}

          <Card title="Report Filters">
            <Card.Section>
              <FormLayout>
                <DatePicker
                  month={month}
                  year={year}
                  onChange={setSelectedDates}
                  onMonthChange={handleMonthChange}
                  selected={selectedDates}
                  allowRange
                />
                <Stack spacing="tight">
                  <Button primary onClick={loadReport} loading={loading}>
                    Generate Report
                  </Button>
                  <Button
                    onClick={downloadExcel}
                    disabled={loading || !loaded}
                  >
                    Download Excel
                  </Button>
                </Stack>
              </FormLayout>
            </Card.Section>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <Card title="Per-Order Payment Report">
            <Card.Section>
              {loading ? (
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <Spinner accessibilityLabel="Loading payment report" size="large" />
                </div>
              ) : hasOrders ? (
                <DataTable
                  columnContentTypes={[
                    "text",
                    "text",
                    "text",
                    "text",
                    "numeric",
                    "numeric",
                    "numeric",
                    "numeric",
                    "numeric",
                  ]}
                  headings={[
                    "Order #",
                    "Date",
                    "Customer",
                    "Service Type",
                    "Reg Total",
                    "Shipping",
                    "Fees",
                    "Taxes",
                    "Total (Bank)",
                  ]}
                  rows={rows}
                  verticalAlign="middle"
                />
              ) : loaded ? (
                <TextStyle variation="subdued">
                  No orders found for the selected date range.
                </TextStyle>
              ) : (
                <TextStyle variation="subdued">
                  Select a date range and click Generate Report.
                </TextStyle>
              )}
            </Card.Section>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
